import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Card, Dropdown } from 'react-bootstrap';
import { FaGraduationCap, FaPlus } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import './AdminEducation.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import Navbar from "../../Pages/Navbar/AdminNavbar";
import BaseUrl from '../../ApiServices/BaseUrl'

// Helper function to filter options for dropdown
const filterOptions = (options, searchTerm) => {
  if (!searchTerm) return options;
  return options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

const CustomDropdown = ({
  title,
  field,
  options,
  selectedValue,
  onSelect,
  required = false,
  menuClassName = ''
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = filterOptions(options, localSearchTerm);

  return (
    <div>
      <Dropdown
        className="mb-3"
        show={isOpen}
        onToggle={(isOpen) => setIsOpen(isOpen)}
      >
        <Dropdown.Toggle
          variant="light"
          className="language-select w-100 d-flex justify-content-between align-items-center"
          id={`dropdown-${field}`}
        >
          {selectedValue || `${title}${required ? ' *' : ''}`}
        </Dropdown.Toggle>
        <Dropdown.Menu className={`w-100 ${menuClassName}`}>
          <div className="px-2 py-1">
            <Form.Control
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="mb-2"
              autoFocus
            />
          </div>
          <div>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <Dropdown.Item
                  key={option}
                  active={selectedValue === option}
                  onClick={() => {
                    onSelect(field, option);
                    setLocalSearchTerm('');
                    setIsOpen(false);
                  }}
                >
                  {option}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item disabled>No matches found</Dropdown.Item>
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

const AdminEducation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const userId = location.state?.userId || user.id || '';

  const [formData, setFormData] = useState({
    user_id: userId,
    education: [{
      level: '',
      course: '',
      status: '',
      completion_year: ''
    }]
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchJobSeekerData = async () => {
      try {
        const response = await axios.get(`${BaseUrl}/api/job-seeker/${userId}`);

        if (response.data && response.data.education) {
          let educationData = [];
          if (typeof response.data.education === 'string') {
            try {
              educationData = JSON.parse(response.data.education);
            } catch (e) {
              console.error("Error parsing education data:", e);
              educationData = [{
                level: '',
                course: '',
                status: '',
                completion_year: ''
              }];
            }
          } else if (Array.isArray(response.data.education)) {
            educationData = response.data.education;
          } else {
            educationData = [response.data.education];
          }

          if (educationData.length === 0) {
            educationData = [{
              level: '',
              course: '',
              status: '',
              completion_year: ''
            }];
          }

          setFormData(prev => ({
            ...prev,
            education: educationData
          }));
          setIsSaved(true);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Error fetching job seeker data:', err);
          setError(err.response?.data?.message || 'Failed to fetch education data');
        }
      }
    };

    if (userId) {
      fetchJobSeekerData();
    }
  }, [userId]);

  const handleSelect = (field, value, index = 0) => {
    setFormData(prev => {
      const updatedEducation = [...prev.education];
      updatedEducation[index] = {
        ...updatedEducation[index],
        [field]: value
      };
      return {
        ...prev,
        education: updatedEducation
      };
    });
    setIsSaved(false);
  };

  const addAdditionalEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [
        ...prev.education,
        {
          level: '',
          course: '',
          status: '',
          completion_year: ''
        }
      ]
    }));
    setIsSaved(false);
  };

  const removeEducation = (index) => {
    if (formData.education.length <= 1) return;

    setFormData(prev => {
      const updatedEducation = [...prev.education];
      updatedEducation.splice(index, 1);
      return {
        ...prev,
        education: updatedEducation
      };
    });
    setIsSaved(false);
  };

  const saveData = async () => {
    setIsLoading(true);
    setError(null);

    Swal.fire({
      title: 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const educationToSend = formData.education.filter(edu =>
        edu.level || edu.course || edu.status || edu.completion_year
      );

      const dataToSend = {
        ...formData,
        education: educationToSend.length > 0 ? educationToSend : null
      };

      const response = await axios.post(`${BaseUrl}/api/job-seeker`, dataToSend);

      await Swal.fire({
        title: 'Success!',
        text: 'Your education details have been saved successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });

      setIsSaved(true);
      return response.data;
    } catch (err) {
      console.error('Error saving data:', err);

      await Swal.fire({
        title: 'Error!',
        text: err.response?.data?.message || 'Failed to save education details. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });

      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const validateFormData = async () => {
    for (let i = 0; i < formData.education.length; i++) {
      const edu = formData.education[i];
      if (edu.level || edu.completion_year) {
        if (!edu.level) {
          await Swal.fire({
            title: 'Missing Information',
            text: `Please select education level for education #${i + 1}`,
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3085d6',
          });
          return false;
        }
        // if (!edu.course) {
        //   await Swal.fire({
        //     title: 'Missing Information',
        //     text: `Please select course duration for education #${i + 1}`,
        //     icon: 'warning',
        //     confirmButtonText: 'OK',
        //     confirmButtonColor: '#3085d6',
        //   });
        //   return false;
        // }
        // if (!edu.status) {
        //   await Swal.fire({
        //     title: 'Missing Information',
        //     text: `Please select completion status for education #${i + 1}`,
        //     icon: 'warning',
        //     confirmButtonText: 'OK',
        //     confirmButtonColor: '#3085d6',
        //   });
        //   return false;
        // }
        // REMOVED the mandatory check for completion_year
        // if (!edu.completion_year) {
        //   await Swal.fire({
        //     title: 'Missing Information',
        //     text: `Please select completion year for education #${i + 1}`,
        //     icon: 'warning',
        //     confirmButtonText: 'OK',
        //     confirmButtonColor: '#3085d6',
        //   });
        //   return false;
        // }
      }
    }

    const hasValidEducation = formData.education.some(edu =>
      edu.level // Removed completion_year from validation
    );

    if (!hasValidEducation) {
      await Swal.fire({
        title: 'Missing Information',
        text: 'Please fill in all required fields for at least one education',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    return true;
  };

  const handleSaveNext = async () => {
    const isValid = await validateFormData();
    if (!isValid) return;

    try {
      await saveData();
      window.scrollTo(0, 0);
      navigate('/adminresume', { state: { userId } });
    } catch (err) {
      // Error already handled in saveData
    }
  };

  const handleBack = () => {
    navigate('/adminprevious', { state: { userId } });
  };

  const educationLevels = [
    'Below 10th', 'High School', '12th', 'Diploma', 'BSC Nursing', 'MSC Nursing',
    'Diploma in Physiotherapy', 'Bachelor (BPT)', 'Master (MPT)',
    'Bachelor', 'Master', 'Doctorate', 'University',
    'Associate Degree', 'Post Graduate', 'PhD'
    , 'ITI (Turner / Machinist)', 'Diploma in Mechanical', 'CNC training course'
  ];

  const courseDurations = [
    '1 Month', '2 Months', '3 Months', '4 Months',
    '5 Months', '6 Months', '1 year', '2 years',
    '3 years', '4 years', '5 years', '6 years'
  ];

  const years = Array.from({ length: 30 }, (_, i) => (new Date().getFullYear() - i).toString());

  const selectedButtonStyle = {
    backgroundColor: '#0d6efd',
    color: 'white',
    borderColor: '#0d47a1'
  };

  // Get first 4 options for buttons
  const getFirstFourOptions = (options) => options.slice(0, 4);
  // Get remaining options for dropdown
  const getRemainingOptions = (options) => options.slice(4);

  return (
    <>
      <Navbar />
      <div className="skills-wrapper" style={{ marginTop: "84px" }}>
        <Container className="mt-4 px-4 pb-5">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <Card className="shadow border-0 education-card">
          <Card.Header className="education-header">
            <h2 className="m-0 text-white text-center">
              <i className="bi bi-mortarboard me-2"></i>
              Education
            </h2>
          </Card.Header>
          <Card.Body className="education-body">
            {/* Education Sections */}
            {formData.education.map((edu, index) => (
              <div key={index} className="education-section-container mb-4 p-3 border rounded">
                {index > 0 && (
                  <div className="d-flex justify-content-end mb-3">
                    <Button
                      variant="danger"
                      onClick={() => removeEducation(index)}
                      disabled={isLoading}
                    >
                      Remove Education
                    </Button>
                  </div>
                )}

                <h4>{index === 0 ? 'What is your Education?' : `Additional Education #${index}`}<span className="text-danger">*</span></h4>

                {/* Education Level Section - 4 buttons + dropdown */}
                <div className="education-section mb-4">
                  <Row className="mt-3">
                    {getFirstFourOptions(educationLevels).map((level, idx) => (
                      <Col xs={6} sm={6} md={3} className="mb-3" key={idx}>
                        <Button
                          className={`w-100 education-btn ${edu.level === level ? 'selected-button' : ''}`}
                          onClick={() => handleSelect('level', level, index)}
                          disabled={isLoading}
                          style={edu.level === level ? selectedButtonStyle : {}}
                        >
                          {level}
                        </Button>
                      </Col>
                    ))}
                  </Row>

                  {/* Custom Dropdown for remaining education levels */}
                  <div className="mt-3">
                    <CustomDropdown
                      title="Select other Education"
                      field="level"
                      options={getRemainingOptions(educationLevels)}
                      selectedValue={edu.level && !getFirstFourOptions(educationLevels).includes(edu.level) ? edu.level : ''}
                      onSelect={(field, value) => handleSelect(field, value, index)}
                      menuClassName="personalinfo-nationality-menu"
                    />
                  </div>
                </div>

                {/* Course Duration Section - 4 buttons + dropdown */}
                <div className="duration-section mb-4">
                  <h4>What is the duration of your course?</h4>
                  <Row className="mt-3">
                    {getFirstFourOptions(courseDurations).map((duration, idx) => (
                      <Col xs={6} sm={6} md={3} className="mb-3" key={idx}>
                        <Button
                          className={`w-100 duration-btn ${edu.course === duration ? 'selected-button' : ''}`}
                          onClick={() => handleSelect('course', duration, index)}
                          disabled={isLoading}
                          style={edu.course === duration ? selectedButtonStyle : {}}
                        >
                          {duration}
                        </Button>
                      </Col>
                    ))}
                  </Row>

                  {/* Custom Dropdown for remaining course durations */}
                  <div className="mt-3">
                    <CustomDropdown
                      title="Select other duration"
                      field="course"
                      options={getRemainingOptions(courseDurations)}
                      selectedValue={edu.course && !getFirstFourOptions(courseDurations).includes(edu.course) ? edu.course : ''}
                      onSelect={(field, value) => handleSelect(field, value, index)}
                      menuClassName="personalinfo-nationality-menu"
                    />
                  </div>
                </div>

                {/* Completion Status Section */}
                <div className="completion-section mb-4">
                  <h4>Did you complete this course?</h4>
                  <Row className="mt-3">
                    {['Yes', 'Pending', 'No'].map((status, idx) => (
                      <Col xs={4} key={idx} className="mb-3">
                        <Button
                          className={`w-100 completion-btn ${edu.status === status ? 'selected-button' : ''}`}
                          onClick={() => handleSelect('status', status, index)}
                          disabled={isLoading}
                          style={edu.status === status ? selectedButtonStyle : {}}
                        >
                          {status}
                        </Button>
                      </Col>
                    ))}
                  </Row>
                </div>

                {/* Completion Year */}
                {/* Completion Year */}
                <div className="completion-year-section mb-4">
                  <h4>What was the completion year of your course?</h4> {/* Removed <span className="text-danger">*</span> */}
                  <Form.Select
                    className="completion-year-select"
                    value={edu.completion_year || ''}
                    onChange={(e) => handleSelect('completion_year', e.target.value, index)}
                    disabled={isLoading}
                  >
                    <option value="">Select the year of your course</option>
                    {years.map(year => (
                      <option key={`${index}-${year}`} value={year}>{year}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            ))}

            {/* Add Education Button */}
            <div className="d-flex justify-content-end" style={{ marginTop: '-15px', marginBottom: '80px' }}>
              <Button
                className="add-education-btn"
                onClick={addAdditionalEducation}
                disabled={isLoading}
              >
                <FaPlus className="me-1" /> Add Other Education
              </Button>
            </div>

            {/* Navigation Buttons */}
            <div className="additional-section mt-3 mb-4">
              <div className="d-flex justify-content-between gap-2" style={{ width: "100%" }}>
                <button
                  className="nav-btn"
                  style={{ flex: 1 }}
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  Back
                </button>
                <button
                  className="nav-btn"
                  style={{ flex: 1 }}
                  onClick={handleSaveNext}
                  disabled={isLoading}
                >
                  Next
                </button>
              </div>
            </div>
          </Card.Body>
        </Card>
        </Container>
      </div>
    </>
  );
};

export default AdminEducation;
